/**
 * Notification writer — minimal helpers for inserting rows into
 * public.notifications from across the app. Today only the SOP engine
 * calls in; later phases can add helpers for workflow / SLA emitters.
 *
 * Schema: supabase/migrations/00001_initial_schema.sql (table notifications).
 * Reader: services/supabaseService.ts#getNotifications + NotificationsCockpit.
 *
 * Two channel families:
 *   - in_app: write a row to public.notifications (this module)
 *   - email / slack: forward to the /api/notifications/dispatch Vercel
 *     function so the secrets stay server-side
 */

import { supabase } from '@/lib/supabase'
import { generateId } from '@/lib/utils'
import type { Sop, SopFinding, NotifyChannel } from '@/types/sop'
import type { SopEvent } from '@/services/sopEngine'

interface SopNotificationArgs {
  sop: Sop
  event: SopEvent
  findings: SopFinding[]
  roles: string[]
}

/**
 * Write a "sop_finding" notification. We piggyback on the existing
 * `type='system'` bucket so the cockpit's System filter surfaces it
 * without a code change, and use `category='sop_finding'` so future
 * filters can split them off.
 */
export async function writeSopNotification(args: SopNotificationArgs): Promise<void> {
  const { sop, event, findings, roles } = args
  const priority =
    sop.severity === 'block' ? 'Critical'
    : sop.severity === 'warn' ? 'High'
    : 'Medium'

  const summary = findings
    .map((f) => f.message)
    .slice(0, 3)
    .join(' · ') + (findings.length > 3 ? ` (+${findings.length - 3} more)` : '')

  const row = {
    id: `ntf_sop_${generateId()}`,
    type: 'system',
    category: 'sop_finding',
    priority,
    title: `SOP "${sop.name}" flagged ${event.entityType}/${event.entityId}`,
    summary: summary.slice(0, 500),
    entity_type: event.entityType,
    entity_id: event.entityId,
    entity_name: event.entityName ?? null,
    portfolio_id: event.portfolioId ?? null,
    portfolio_name: event.portfolioName ?? null,
    requested_by: event.actor ?? null,
    requested_by_name: event.actorName ?? null,
    assigned_role: roles[0] ?? null,
    context_data: {
      sopId: sop.id,
      sopName: sop.name,
      severity: sop.severity,
      scopeKind: sop.scopeKind,
      scopeTarget: sop.scopeTarget,
      findings,
    },
    read: false,
    dismissed: false,
  }

  const { error } = await supabase.from('notifications').insert(row)
  if (error) {
    // Don't throw — the SOP engine logs and moves on.
    throw new Error(`Failed to write SOP notification: ${error.message}`)
  }
}

// ─── Request-decision notices (worker-facing) ────────────────────────────────

export type RequestDecisionKind = 'timesheet' | 'pto' | 'skill_claim' | 'cost_transfer' | 'shift_swap'

/**
 * Write a "your request was decided" notice targeted at ONE worker
 * (`assigned_to`), so approve/reject outcomes land in the bell the moment a
 * manager decides — the read-side complement of the My Requests card, which
 * only shows outcomes when the worker goes looking. Fired from every decide
 * service: timesheet submissions, PTO requests, skill claims, cost transfers,
 * shift swaps.
 *
 * `type: 'workflow_update'` — an informational notice (System tab), NOT
 * 'workflow_approval', which the cockpit renders with Approve/Reject buttons
 * (an action request, wrong for an already-decided item).
 *
 * NEVER throws: a decision must not fail (or appear to fail) because the
 * notice insert failed — errors are logged and swallowed.
 */
export async function writeRequestDecisionNotification(args: {
  /** The requesting worker who should see the notice (assigned_to). */
  workerId: string
  workerName?: string | null
  kind: RequestDecisionKind
  decision: 'approved' | 'rejected'
  /** Worker-facing headline, e.g. `Timesheet approved — week of 2026-06-15`. */
  title: string
  /** Optional detail: decider, note/reason, hours, dates. */
  summary?: string | null
  /** The decided record's id (entity_id; entity_type = kind). */
  entityId: string
  decidedBy?: string | null
  decidedByName?: string | null
  contextData?: Record<string, unknown>
}): Promise<void> {
  const row = {
    id: `ntf_dec_${generateId()}`,
    type: 'workflow_update',
    category: 'request_decision',
    priority: args.decision === 'rejected' ? 'High' : 'Medium',
    title: args.title.slice(0, 200),
    summary: args.summary ? args.summary.slice(0, 500) : null,
    entity_type: args.kind,
    entity_id: args.entityId,
    requested_by: args.decidedBy ?? null,
    requested_by_name: args.decidedByName ?? null,
    assigned_to: args.workerId,
    assigned_to_name: args.workerName ?? null,
    context_data: { kind: args.kind, decision: args.decision, ...(args.contextData ?? {}) },
    read: false,
    dismissed: false,
  }
  try {
    const { error } = await supabase.from('notifications').insert(row)
    if (error) console.warn('[notificationService] decision notice insert failed:', error.message)
  } catch (err) {
    console.warn('[notificationService] decision notice insert failed:', err)
  }
}

// ─── Cockpit write-back (read state + decisions) ─────────────────────────────
// The Notifications page previously held read/approve state in local React
// state only — refresh and everything reverted, and a card "approval" moved
// nothing. These helpers make the cockpit real: durable read/unread, and a
// decision that persists on the row AND advances the linked workflow instance.
// All best-effort and non-throwing: the UI keeps its optimistic local state,
// so mock-mode (no reachable DB) still behaves exactly as before.

/** Persist read/unread for one or more notifications. Never throws. */
export async function setNotificationsRead(ids: string[], read: boolean): Promise<void> {
  if (ids.length === 0) return
  try {
    const { error } = await supabase.from('notifications').update({ read }).in('id', ids)
    if (error) console.warn('[notificationService] setNotificationsRead failed:', error.message)
  } catch (err) {
    console.warn('[notificationService] setNotificationsRead failed:', err)
  }
}

/**
 * Persist a cockpit decision: stamps `action_taken` (+ read) on the
 * notification, and — for approve/reject with a linked workflow instance —
 * advances that instance (status + end_time + appended history event),
 * mirroring the timesheet's advanceWorkflow shape. Defer only records the
 * action. Never throws.
 */
export async function recordNotificationDecision(args: {
  notificationId: string
  action: 'approved' | 'rejected' | 'deferred'
  workflowInstanceId?: string | null
  actorId?: string | null
}): Promise<void> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ action_taken: args.action, read: true })
      .eq('id', args.notificationId)
    if (error) console.warn('[notificationService] decision stamp failed:', error.message)
  } catch (err) {
    console.warn('[notificationService] decision stamp failed:', err)
  }

  if (!args.workflowInstanceId || args.action === 'deferred') return
  try {
    const now = new Date().toISOString()
    const { data } = await supabase
      .from('workflow_instances')
      .select('history')
      .eq('id', args.workflowInstanceId)
      .single()
    const history = ((data as { history: unknown[] | null } | null)?.history) ?? []
    const { error } = await supabase
      .from('workflow_instances')
      .update({
        status: args.action,
        end_time: now,
        history: [
          ...history,
          { event: args.action, at: now, actor_id: args.actorId ?? null, source: 'notifications-cockpit' },
        ],
      })
      .eq('id', args.workflowInstanceId)
    if (error) console.warn('[notificationService] workflow advance failed:', error.message)
  } catch (err) {
    console.warn('[notificationService] workflow advance failed:', err)
  }
}

/**
 * Dispatch email/slack channels for a SOP finding via the server-side
 * dispatcher. The browser doesn't carry the dispatcher secret, so this
 * helper short-circuits in that environment — only the cron (which knows
 * NOTIFY_SECRET) actually triggers outbound sends. The in_app channel
 * still fires from the browser via writeSopNotification.
 *
 * Callers in the browser: pass channels; this function inspects the env
 * for the dispatcher secret and silently no-ops when absent so engine
 * errors don't bubble up at every save.
 */
export async function dispatchExternalChannels(args: {
  sop: Sop
  event: SopEvent
  findings: SopFinding[]
  channels: NotifyChannel[]
  /** Override the dispatcher base URL. Defaults to /api/notifications/dispatch. */
  baseUrl?: string
}): Promise<void> {
  const external = args.channels.filter((c) => c.kind === 'email' || c.kind === 'slack')
  if (external.length === 0) return

  // The secret only exists server-side. When this runs in the browser the
  // bearer header will be missing and the dispatcher will return 401 —
  // skip the fetch entirely instead of producing noise in the console.
  const secret = (typeof process !== 'undefined' ? process.env?.NOTIFY_SECRET : undefined) ?? null
  if (!secret) return

  const summary = args.findings.map((f) => f.message).slice(0, 5).join(' · ')
  const subject = `[${args.sop.severity.toUpperCase()}] ${args.sop.name} — ${args.event.entityType}/${args.event.entityId}`
  const body = [
    `SOP: ${args.sop.name}`,
    `Severity: ${args.sop.severity}`,
    `Entity: ${args.event.entityType}/${args.event.entityId}`,
    args.event.entityName ? `Name: ${args.event.entityName}` : null,
    '',
    'Findings:',
    ...args.findings.map((f) => `- ${f.clause}: ${f.message}`),
  ].filter(Boolean).join('\n')

  const channels = external.map((c) => {
    if (c.kind === 'email') return { kind: 'email', to: c.to ?? [], subject, body }
    return { kind: 'slack', webhook: c.webhook, text: `*${subject}*\n${summary}` }
  })

  const url = (args.baseUrl ?? '/api/notifications/dispatch')
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${secret}` },
      body: JSON.stringify({ channels }),
    })
  } catch {
    // Network failures are swallowed — engine logs separately.
  }
}
